import { useMutation } from '@apollo/client';
import useAppSelector from '../../app/redux/useAppSelector';
import { gql } from '../../graphql/generated/gql';
import type {
  TopicPostsOrder,
  TopicPostsType,
} from '../../graphql/generated/graphql';
import topicPolicyCheck, { TopicPolicyAction } from '../topicPolicyCheck';
import { useAllTopicMap } from '../useAllTopics';

const PIN_POST_MUTATION = gql(/** @complexity 6 */ `
  mutation pinPost(
    $first: Int!
    $pinnedOrder: Int!
    $orderBy: TopicPostsOrder!
    $postId: ID!
    $topicId: ID!
    $type: TopicPostsType
  ) {
    pinPost(postId: $postId, topicId: $topicId, pinnedOrder: $pinnedOrder) {
      id
      posts(first: $first, orderBy: $orderBy, type: $type) {
        edges {
          cursor
          node {
            id
          }
          pinnedOrder
        }
      }
    }
  }
`);

const UNPIN_POST_MUTATION = gql(/** @complexity 6 */ `
  mutation unpinPost(
    $first: Int!
    $orderBy: TopicPostsOrder!
    $postId: ID!
    $topicId: ID!
    $type: TopicPostsType
  ) {
    unpinPost(postId: $postId, topicId: $topicId) {
      id
      posts(first: $first, orderBy: $orderBy, type: $type) {
        edges {
          cursor
          node {
            id
          }
          pinnedOrder
        }
      }
    }
  }
`);

const usePinMutations = ({
  topicId,
  type,
  orderBy,
  postsCount,
}: {
  topicId: string;
  type: TopicPostsType | null;
  orderBy: TopicPostsOrder;
  postsCount: number;
}) => {
  const topicMap = useAllTopicMap();
  const fullTopic = topicMap[topicId];
  const currentUser = useAppSelector((state) => state.session.user);

  const canPin = fullTopic
    ? topicPolicyCheck(TopicPolicyAction.EditTopic, fullTopic, currentUser)
    : false;

  const [pinPostMutate] = useMutation(PIN_POST_MUTATION);
  const [unpinPostMutate] = useMutation(UNPIN_POST_MUTATION);

  const pinPost = canPin
    ? (postId: string, pinnedOrder: number) =>
        pinPostMutate({
          variables: {
            first: postsCount,
            orderBy,
            type,
            topicId,
            postId,
            pinnedOrder,
          },
        })
    : null;

  const unpinPost = canPin
    ? (postId: string) =>
        unpinPostMutate({
          variables: {
            first: postsCount,
            orderBy,
            type,
            topicId,
            postId,
          },
        })
    : null;

  return { pinPost, unpinPost };
};

export default usePinMutations;
